import React, { useState } from 'react';
import { Wrench, Sparkles, Palette, Award, Music, Star, X, Phone, Mail, Hammer, Shield, CheckCircle, ArrowRight, Clock } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const ServicesPage = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const scrollRef4 = useScrollAnimation();
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const services = [
    {
      icon: Wrench,
      title: "Accordage Professionnel",
      description: "Service d'accordage régulier pour maintenir la justesse de votre instrument",
      details: [
        "Accordage traditionnel à l'oreille",
        "Contrôle de la mécanique",
        "Réglage des pédales",
        "Conseil d'entretien personnalisé"
      ],
      image: "/accordage.jpg"
    },
    {
      icon: Hammer,
      title: "Restauration Complète",
      description: "Remise en état intégrale de pianos anciens avec respect des techniques traditionnelles",
      details: [
        "Démontage et diagnostic complet",
        "Remplacement des cordes et marteaux",
        "Restauration du meuble",
        "Remontage et réglages fins"
      ],
      image: "/WhatsApp_Image_restauration.jpeg"
    },
    {
      icon: Palette,
      title: "Projets Créatifs",
      description: "Transformation d'anciens pianos en mobilier design et fonctionnel",
      details: [
        "Bars à partir de pianos à queue",
        "Bibliothèques et étagères",
        "Tables et bureaux uniques",
        "Conception sur mesure"
      ],
      image: "/WhatsApp_Image_creatif.jpeg"
    },
    {
      icon: Award,
      title: "Expertise et Estimation",
      description: "Évaluation professionnelle pour assurance, succession ou vente",
      details: [
        "Expertise certifiée",
        "Rapport détaillé",
        "Estimation de valeur",
        "Conseil en acquisition"
      ],
      image: "/expertise.jpeg"
    }
  ];

  const scrollToContact = () => {
    // Si on est sur la page d'accueil, scroll vers contact
    if (window.location.pathname === '/') {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Sinon, rediriger vers la page d'accueil avec ancre
      window.location.href = '/#contact';
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Nos <span className="heading-accent">Services</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              De l'accordage traditionnel aux projets créatifs les plus audacieux, 
              notre savoir-faire artisanal s'adapte à tous vos besoins musicaux et décoratifs.
            </p>
          </div>

          {/* Avantages */}
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal grid md:grid-cols-4 gap-6 mb-20"
          >
            {[
              { icon: Clock, title: "Intervention Rapide", desc: "Disponibilité sous 48h" },
              { icon: Award, title: "Expertise Reconnue", desc: "Plus de 40 années d'expérience" },
              { icon: Shield, title: "Garantie Qualité", desc: "Travail garanti" },
              { icon: CheckCircle, title: "Devis Gratuit", desc: "Estimation sans engagement" }
            ].map((feature, index) => (
              <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                <feature.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 font-light text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services détaillés */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-3xl font-light text-center text-white mb-16">
              Notre <span className="heading-accent">Savoir-Faire</span>
            </h2>
            
            <div className="space-y-16">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                const isEven = index % 2 === 0;
                
                return (
                  <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-col-dense' : ''}`}>
                    {/* Image */}
                    <div className={`relative ${!isEven ? 'lg:col-start-2' : ''}`}>
                      <div className="relative overflow-hidden rounded-3xl">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full aspect-square object-cover transition-transform duration-700 hover:scale-110"
                          style={{ objectPosition: 'center center', objectFit: 'cover' }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div className="absolute top-6 left-6">
                          <div className="p-3 rounded-full bg-yellow-400/20 backdrop-blur-sm border border-yellow-400/30">
                            <IconComponent className="w-6 h-6 text-yellow-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Contenu */}
                    <div className={`glass-card rounded-3xl p-8 ${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                      <h3 className="text-3xl font-light text-white mb-4">
                        {service.title}
                      </h3>
                      <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
                        {service.description}
                      </p>
                      
                      <div className="space-y-4 mb-8">
                        {service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-center space-x-3">
                            <ArrowRight className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                            <span className="text-white/80 font-light">{detail}</span>
                          </div>
                        ))}
                      </div>
                      
                      <button 
                        onClick={scrollToContact}
                        className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide"
                      >
                        Demander un Devis
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Projet spécial - Bar piano */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef4 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <div className="glass-card rounded-3xl p-12 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-light text-white mb-6">
                  Projet <span className="heading-accent">Exceptionnel</span>
                </h3>
                <p className="text-white/70 font-light text-lg leading-relaxed max-w-3xl mx-auto">
                  Découvrez notre réalisation la plus audacieuse : la transformation d'un piano à queue 
                  hors d'usage en un bar design unique, alliant histoire musicale et fonctionnalité moderne.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="relative">
                  <img
                    src="/image1.jpg"
                    id="services-bar-piano-image"
                    alt="Bar créé à partir d'un piano"
                    className="w-full aspect-square object-cover rounded-2xl"
                  />
                </div>
                
                <div className="space-y-6">
                  <h4 className="text-2xl font-semibold text-white">Du Piano au Bar Design</h4>
                  <p className="text-white/70 font-light leading-relaxed">
                    Ce piano droit, irréparable mais chargé d'histoire, a trouvé une seconde vie
                    en tant que bar d'exception. Conservation de la structure originale,
                    intégration d'un système d'éclairage LED et création d'espaces de rangement optimisés.
                  </p>
                  
                  <div className="space-y-3">
                    {[
                      "Conservation de l'esthétique originale",
                      "Intégration d'éclairage moderne",
                      "Espaces de rangement sur mesure",
                      "Finition haut de gamme"
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        <span className="text-white/80 font-light">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-12">
                <p className="text-white/60 font-light mb-6">
                  Vous avez un projet créatif en tête ? Parlons-en !
                </p>
                <button
                  onClick={scrollToContact}
                  className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide"
                >
                  Discuter de Mon Projet
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action final */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-light text-white mb-6">
              Prêt à Donner Vie à <span className="heading-accent">Votre Projet</span> ?
            </h3>
            <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
              Que ce soit pour un accordage, une restauration ou un projet créatif unique, 
              notre équipe d'artisans passionnés est à votre écoute.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={scrollToContact}
                className="btn-premium px-8 py-4 rounded-full font-semibold tracking-wide"
              >
                Demander un Devis Gratuit
              </button>
              <button 
                onClick={() => setShowAppointmentModal(true)}
                className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-8 py-4 rounded-full font-light tracking-wide transition-all duration-300"
              >
                Prendre Rendez-vous
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de prise de rendez-vous */}
      {showAppointmentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card rounded-3xl max-w-md w-full">
            <div className="p-8 text-center">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-semibold text-white">Prendre Rendez-vous</h3>
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="glass-card rounded-2xl p-6">
                  <Phone className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Appelez-nous</h4>
                  <div className="space-y-2 text-white/70">
                    <p>Magasin : <a href="tel:+33389542003" className="text-yellow-400 hover:text-yellow-300 transition-colors">03 89 54 20 03</a></p>
                    <p>Mobile : <a href="tel:+33607971604" className="text-yellow-400 hover:text-yellow-300 transition-colors">06 07 97 16 04</a></p>
                  </div>
                </div>
                
                <div className="glass-card rounded-2xl p-6">
                  <Mail className="h-8 w-8 text-yellow-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">Écrivez-nous</h4>
                  <p className="text-white/70">
                    <a href="mailto:musique.buchta@gmail.com" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                      musique.buchta@gmail.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="mt-8">
                <button 
                  onClick={() => setShowAppointmentModal(false)}
                  className="bg-white/5 hover:bg-white/10 border border-white/20 hover:border-yellow-400/30 text-white hover:text-yellow-400 px-8 py-3 rounded-full font-light transition-all duration-300 w-full"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;